'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { forwardRef } from '@ironhack/design-system2/components/forward-ref';
import { Select } from '@ironhack/design-system2/components/select';
import { type As, type ComponentWithAs } from '@ironhack/design-system2/types';
import {
  append,
  concat,
  evolve,
  is,
  of,
  pipe,
  pluck,
  prop,
  reduce,
  uniq,
  unnest,
} from 'ramda';
import { type ChangeEvent, type ReactElement, useMemo, useState } from 'react';
import { type Merge, type SetOptional } from 'type-fest';

import { type ArticleCardData } from '@/app/components/client/article-card';
import { Button } from '@/app/components/client/button';
import {
  CardCarousel,
  type CardCarouselCard,
  type CardCarouselCardComponent,
  type CardCarouselCardList,
} from '@/app/components/client/card-carousel';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import {
  type DatoButtonBlock,
  type DatoCardBlock,
  type DatoModuleFormat,
  type DatoUserCardBlock,
} from '@/lib/datocms/types';

type AnyObject = Record<string, unknown>;
type FilterAcc = AnyObject | AnyObject[] | string | string[];

type CardWithFilter = CardCarouselCard & { filterValues: string[] };
type FilterValues = {
  cardsWithFilter: CardWithFilter[];
  filterValues: string[];
};
type FilterValuesOptional = Merge<
  FilterValues,
  { cardsWithFilter: Array<SetOptional<CardWithFilter, 'filterValues'>> }
>;

const getFilterValues = (
  cards: CardCarouselCard[],
  filterField?: string
): FilterValuesOptional =>
  filterField
    ? reduce(
        (acc: FilterValues, card: CardCarouselCard): FilterValues => {
          const filterValues = pipe(
            reduce((innerAcc: FilterAcc, pathPart: string) => {
              const fn = (is(Array, innerAcc) ? pluck : prop) as (
                part: string,
                accu: FilterAcc
              ) => FilterAcc;

              return fn(pathPart, innerAcc);
            }, card),
            of,
            unnest
          )(filterField.split('.')) as string[];

          return evolve(
            {
              cardsWithFilter: append({ ...card, filterValues }),
              filterValues: pipe(concat(filterValues), uniq),
            },
            acc
          );
        },
        { cardsWithFilter: [], filterValues: [] }
      )(cards)
    : { cardsWithFilter: cards, filterValues: [] };

export type Carousel<TCard extends CardCarouselCard = DatoCardBlock> = {
  button?: [DatoButtonBlock];
  cardComponent: CardCarouselCardComponent<TCard>;
  cards: [CardCarouselCardList<TCard>];
  filterField?: string;
  filterPlaceholder?: string;
  headingProps?: TextProps | null;
  moduleFormat: [DatoModuleFormat];
  smallScreenCardDirection?: 'horizontal' | 'vertical';
  subtitle?: string;
  title?: string;
};

type GenericProps<TCard extends CardCarouselCard> = {
  data: TCard extends DatoCardBlock
    ? SetOptional<Carousel<TCard>, 'cardComponent'>
    : Carousel<TCard>;
} & BoxProps;

// Can only be called with type matching between component and array of cards
type Props =
  | GenericProps<DatoCardBlock>
  | GenericProps<DatoUserCardBlock>
  | GenericProps<ArticleCardData>;

export const Carousel: ComponentWithAs<As, Props> = forwardRef(
  (props: Props, ref): ReactElement | null => {
    const { data, title: navTitle, ...boxProps } = props;
    const {
      button: buttonBlock,
      cardComponent,
      cards: cardsBlock,
      filterField,
      filterPlaceholder,
      headingProps,
      moduleFormat: [moduleFormat],
      smallScreenCardDirection,
      subtitle,
      title,
    } = data;

    const button = buttonBlock?.[0];
    const { cards = [], visibleCards = '4' } = cardsBlock?.[0] ?? {};

    const [filterString, setFilterString] = useState('');
    const { cardsWithFilter, filterValues } = useMemo(
      () => getFilterValues(cards, filterField),
      [cards, filterField]
    );
    const filteredCards = useMemo(
      () =>
        filterString
          ? cardsWithFilter.filter(({ filterValues: cardFilterValues }) =>
              cardFilterValues?.includes(filterString)
            )
          : cardsWithFilter,
      [cardsWithFilter, filterString]
    );

    const handleFilterChange = (event: ChangeEvent<HTMLSelectElement>) => {
      const filterValue = event.target.value;
      setFilterString(filterValue);
    };

    if (cards.length === 0) return null;

    return (
      <ModuleWrapper
        innerProps={{ direction: 'column', gap: 3 }}
        moduleFormat={moduleFormat}
        outerProps={boxProps}
        ref={ref}
        title={navTitle}
      >
        {title && (
          <Text as="h2" textStyle={['3xl', null, '4xl']} {...headingProps}>
            {title}
          </Text>
        )}
        {subtitle && <Text textStyle="l">{subtitle}</Text>}
        {filterField && (
          <Select
            onChange={handleFilterChange}
            placeholder={filterPlaceholder}
            w={['full', null, '50%']}
          >
            {filterValues.map((filterValue) => (
              <option key={filterValue} value={filterValue}>
                {filterValue}
              </option>
            ))}
          </Select>
        )}
        {filteredCards.length > 0 && (
          <CardCarousel
            cardComponent={cardComponent}
            // @ts-expect-error -- Ok if accept different kinds of card arrays
            cards={filteredCards}
            smallScreenCardDirection={smallScreenCardDirection}
            visibleCards={Number.parseInt(visibleCards)}
          />
        )}
        {button && <Button alignSelf="center" w="fit-content" {...button} />}
      </ModuleWrapper>
    );
  }
);
